// Helpers & Utils

// Components
import { Stack } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { AquariusButtonGroup } from "@smartrr/shared/customerPortal/components/AquariusButtonGroup";
import { DotSpinner } from "@smartrr/shared/customerPortal/components/DotSpinner";
import { ICancellationReason } from "@smartrr/shared/entities/CancellationRelationship";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { ISmartrrSellingPlan } from "@smartrr/shared/entities/SellingPlanGroup";
import { capitalizeFirstLetter } from "@smartrr/shared/utils/capitalizeFirstLetter";
import { ensureShopifyGid } from "@smartrr/shared/utils/ensureShopifyGid";
import { localize } from "@smartrr/shared/utils/sharedTranslations/localize";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { typedCustomerFrontendApi } from "@customer-app/shared/typedCustomerFrontendApi";
import { updateLocalCustomerPurchaseState } from "@customer-app/state/actionCreators/customerPurchaseStates";
import { useCloseModal } from "@customer-app/state/actionCreators/modal";
import {
  useSmartrrCustomerDispatch,
  useSmartrrCustomerSelector,
} from "@customer-app/state/typedCustomerReduxHooks";

interface IEditFreqViewProps {
  delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>;
  setIconButtonInactive: () => void;
  sellingPlanOptions: ISmartrrSellingPlan[];
  saveUserCancellationData?: (cancel?: boolean) => void;
  cancellationReason?: ICancellationReason | undefined;
}

export const EditFreqView = ({
  delivery,
  setIconButtonInactive,
  sellingPlanOptions,
  saveUserCancellationData,
  cancellationReason,
}: IEditFreqViewProps): JSX.Element => {
  const localizeShorthand = "subscriptions.yourSubscriptions.manageSubscriptionModal";
  const { purchaseState } = delivery;

  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useSmartrrCustomerDispatch();
  const closeModal = useCloseModal();
  const organization = useSmartrrCustomerSelector(state => state.auth.organization);
  const stackRef = useRef<HTMLDivElement>(null);
  const [selectedSellingPlan, setSelectedSellingPlan] = useState<ISmartrrSellingPlan>();

  // TODO: Move to another file
  const changeFrequency = useCallback(
    async (sellingPlanId: number) => {
      setIsLoading(true);

      try {
        const res = await typedCustomerFrontendApi.putReq("/purchase-state/:id/selling-plan", {
          params: {
            id: purchaseState.id,
          },
          reqBody: {
            sellingPlanId: ensureShopifyGid("SellingPlan", sellingPlanId),
          },
        });

        if (res.type === "error") {
          throw new Error(res.message);
        }

        if (organization && res.body.schedule) {
          toast(localize(`${localizeShorthand}.editFreqView.successToast`));
        }

        dispatch(updateLocalCustomerPurchaseState(res.body));
        dispatch({
          type: "PURCHASE_STATE_UPDATE_SELLING_PLAN_SUCCESS",
          payload: {
            purchaseState: res.body,
          },
        });
      } catch {
        toast.error(localize(`${localizeShorthand}.editFreqView.errorToast`));
      } finally {
        setIsLoading(false);
        closeModal();
      }
    },
    [organization, purchaseState]
  );

  useEffect(() => {
    if (sellingPlanOptions.length === 1) {
      setSelectedSellingPlan(sellingPlanOptions[0]);
    }
  }, [sellingPlanOptions]);
  return (
    <React.Fragment>
      {isLoading ? (
        <Stack sx={{ height: stackRef.current?.clientHeight }} alignItems="center" justifyContent="center">
          <DotSpinner />
        </Stack>
      ) : (
        <React.Fragment>
          <Stack
            spacing="15px"
            justifyContent="center"
            alignItems="center"
            ref={stackRef}
            className={prefixClassName("sub-program-frequencies")}
          >
            {sellingPlanOptions.map(plan => (
              <AquariusButton
                key={plan.shopifyNumericId}
                onClick={() => setSelectedSellingPlan(plan)}
                selected={plan.shopifyNumericId === selectedSellingPlan?.shopifyNumericId}
              >
                {capitalizeFirstLetter(plan.name)}
              </AquariusButton>
            ))}
          </Stack>
          <AquariusButtonGroup topBorder sx={{ marginTop: "15px" }}>
            <AquariusButton onClick={setIconButtonInactive}>{localize(`general.cancelBtn`)}</AquariusButton>
            {selectedSellingPlan ? (
              <AquariusButton
                filled
                onClick={async () => {
                  await changeFrequency(selectedSellingPlan.shopifyNumericId);
                  if (cancellationReason && saveUserCancellationData) {
                    saveUserCancellationData();
                  }
                }}
              >
                {localize(`general.confirmBtn`)}
              </AquariusButton>
            ) : (
              <React.Fragment />
            )}
          </AquariusButtonGroup>
          {/* {selectedSellingPlan && discountAmt() ? (
            <Box sx={{ display: "flex", textAlign: "center", justifyContent: "center", marginTop: "10px" }}>
              <Typography variant="caption1">
                {localize(`${localizeShorthand}.editFreqView.saveCaption`, {
                  discountAmt: discountAmt(),
                })}
              </Typography>
            </Box>
          ) : null} */}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};
